import {match} from 'ts-pattern';

import {always, clone, defaultTo, isEmpty} from 'ramda';

import {
  PutReceiveNoteItemCalculationResponse,
  ReceiveNoteItemChangedField,
  usePutReceiveNoteItemCalculationMutation,
} from '@dms/api/warehouse';
import {useFormRecalculation} from '@dms/warehouse/shared-components';

import {sanitizeObject} from 'shared/web';

import {ReceiveNoteItemFormType} from '../types/ReceiveNoteItemDetailSchema';

const ALLOWED_OBJECTS = ['receiveNoteItem', 'deliveryItem', 'supplierArticle'];

const ALLOWED_OBJECT_KEYS = [
  'quantity',
  'unitPrice',
  'quantityVerified',
  'supplierQuantity',
  'warehouseQuantity',
  'totalPrice',
  'totalPriceDelivery',
  'purchasePriceVerified',
  'stockValueVerified',
];

/**
 * Custom hook for receive note item form recalculation
 * Wraps the generic useFormRecalculation hook with receive note item specific configuration
 */
export function useReceiveNoteItemFormRecalculation() {
  const [calculatePrices, {isLoading: isCalculateLoading}] =
    usePutReceiveNoteItemCalculationMutation();

  const formRecalculation = useFormRecalculation<
    ReceiveNoteItemFormType,
    {
      changedFieldValue: {field: ReceiveNoteItemChangedField};
      currentFeFieldsValues: {
        quantity: number | undefined;
        purchasePrice: number | undefined;
        totalPrice: number | undefined;
        deliveryItem: {
          quantityVerified: number | undefined;
          unitPrice: number | undefined;
          totalPriceDelivery: number | undefined;
        };
        supplierArticle: {
          supplierQuantity: number | undefined;
          warehouseQuantity: number | undefined;
        };
      };
    },
    PutReceiveNoteItemCalculationResponse
  >({
    allowedFields: ALLOWED_OBJECT_KEYS,
    allowedObjects: ALLOWED_OBJECTS,
    recalculateMutation: (params) => ({
      unwrap: () =>
        calculatePrices({
          body: params.body,
          signal: params.signal,
        }).unwrap(),
    }),
    requestConfig: {},
    buildRequestBody: (values, changedField) => ({
      changedFieldValue: {
        field: changedField as ReceiveNoteItemChangedField,
      },
      currentFeFieldsValues: {
        quantity: values.receiveNoteItem?.quantity,
        purchasePrice: values.receiveNoteItem?.unitPrice,
        totalPrice: values.receiveNoteItem?.totalPrice,
        deliveryItem: {
          quantityVerified: values.deliveryItem?.quantityVerified,
          unitPrice: values.deliveryItem?.purchasePriceVerified,
          totalPriceDelivery: values.deliveryItem?.stockValueVerified,
        },
        supplierArticle: {
          supplierQuantity: values.supplierArticle?.supplierQuantity,
          warehouseQuantity: values.supplierArticle?.warehouseQuantity,
        },
      },
    }),
    transformResponse: (response, values) =>
      clone(
        sanitizeObject({
          article: {
            name: values.article?.name,
            manufacturerId: values.article?.manufacturerId,
            catalogNumber: values.article?.catalogNumber,
            warehouseId: values.article?.warehouseId,
            storageLocation: values.article?.storageLocation,
            handlingUnit: values.article?.handlingUnit,
            supplierId: values.article?.supplierId,
          },
          receiveNoteItem: {
            quantity: response.receiveNoteItem.quantity,
            unitPrice: response.receiveNoteItem.purchasePrice,
            totalPrice: response.receiveNoteItem.purchaseValue,
            vatType: defaultTo('S', values.receiveNoteItem?.vatType),
          },
          deliveryItem: {
            quantityVerified: response.deliveryNoteItem.quantityVerified,
            purchasePriceVerified: response.deliveryNoteItem.purchasePrice,
            stockValueVerified: response.deliveryNoteItem.purchaseValue,
            vatType: defaultTo('S', values.deliveryItem?.vatType),
          },
          supplierArticle: {
            supplierQuantity: response.supplierArticle.supplierQuantity,
            supplierUnitId: values.supplierArticle?.supplierUnitId,
            warehouseQuantity: response.supplierArticle.warehouseQuantity,
            warehouseUnitId: values.supplierArticle?.warehouseUnitId,
            supplierOrderingNumber: values.supplierArticle?.supplierOrderingNumber,
            supplierBulkPackageQuantity: values.supplierArticle?.supplierBulkPackageQuantity,
            supplier: values.supplierArticle?.supplier,
            supplierDiscountGroup: values.supplierArticle?.supplierDiscountGroup,
          },
        })
      ),
    isDifferenceEmpty: (allowedDifference) => {
      const isReceiveNoteItemPricesDiffEmpty = allowedDifference?.receiveNoteItem
        ? isEmpty(allowedDifference?.receiveNoteItem)
        : false;
      const isDeliveryItemPricesDiffEmpty = allowedDifference?.deliveryItem
        ? isEmpty(allowedDifference?.deliveryItem)
        : false;
      const isSupplierArticleDiffEmpty = allowedDifference?.supplierArticle
        ? isEmpty(allowedDifference?.supplierArticle)
        : false;

      return (
        isReceiveNoteItemPricesDiffEmpty &&
        isDeliveryItemPricesDiffEmpty &&
        isSupplierArticleDiffEmpty
      );
    },
    mapFieldName: (changedField, changedFieldWithFullPath) =>
      match(changedFieldWithFullPath)
        .with('receiveNoteItem.unitPrice', always('purchasePrice'))
        .with('deliveryItem.purchasePriceVerified', always('unitPrice'))
        .with('deliveryItem.stockValueVerified', always('totalPriceDelivery'))
        .otherwise(always(changedField)),
  });

  return {
    ...formRecalculation,
    isCalculateLoading,
  };
}
